import React, { useEffect, useState } from "react";
import "./PerioChart.css";
import PerioLegend from "./PerioLegend";

/* ===============================
   CONSTANTS - HORIZONTAL VISUAL ORDER
   (Patient Right -> Patient Left)
================================ */
const UPPER_ARCH = [
  "18", "17", "16", "15", "14", "13", "12", "11",
  "21", "22", "23", "24", "25", "26", "27", "28"
];

const LOWER_ARCH = [
  "48", "47", "46", "45", "44", "43", "42", "41",
  "31", "32", "33", "34", "35", "36", "37", "38"
];

/* Sites per surface */
const SITES_FACIAL: string[] = ["MB", "B", "DB"];
const SITES_LINGUAL: string[] = ["DL", "L", "ML"];

/* ===============================
   HELPERS
================================ */
function pocketClass(depth: number | string | null | undefined): string {
  if (depth == null || depth === "") return "";
  const n = typeof depth === "number" ? depth : Number(depth);
  if (isNaN(n)) return "";
  if (n >= 6) return "deep-pocket";
  if (n >= 4) return "moderate-pocket";
  return "healthy-pocket";
}

export interface PerioRow {
  mobility?: number;
  MB?: number | null;
  B?: number | null;
  DB?: number | null;
  DL?: number | null;
  L?: number | null;
  ML?: number | null;
  [key: string]: number | null | undefined;
}

export interface PerioChartProps {
  value?: Record<string, PerioRow>;
  onChange?: (data: Record<string, PerioRow>) => void;
  readOnly?: boolean;
}

export default function PerioChart({
  value = {},
  onChange,
  readOnly = false
}: PerioChartProps) {
  const [data, setData] = useState<Record<string, PerioRow>>({});

  useEffect(() => {
    setData(value || {});
  }, [value]);

  /* ===============================
     HANDLERS
  ================================= */
  const updateData = (tooth: string, field: string, val: string) => {
    if (readOnly) return;
    const numVal = val === "" ? null : Number(val);
    const next = {
      ...data,
      [tooth]: {
        ...data[tooth],
        [field]: numVal
      }
    };
    setData(next);
    onChange?.(next);
  };

  /* ===============================
     RENDER SINGLE TOOTH COLUMN
  ================================= */
  const renderToothCol = (tooth: string, _isUpper: boolean) => {
    const row = data[tooth] || {};
    const mobilityVal = row.mobility ?? 0;
    const mobClass = `mob-select${mobilityVal > 0 ? " mob-elevated" : ""}`;

    return (
      <div key={tooth} className="tooth-col">

        {/* MOBILITY (Top) */}
        <div className="cell-mobility">
          <select
            value={mobilityVal}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateData(tooth, "mobility", e.target.value)}
            disabled={readOnly}
            className={mobClass}
            title={`Tooth ${tooth} — Mobility`}
          >
            <option value={0}>0</option>
            <option value={1}>I</option>
            <option value={2}>II</option>
            <option value={3}>III</option>
          </select>
        </div>

        {/* FACIAL SITES (MB, B, DB) */}
        <div className="site-row-label">F</div>
        <div className="site-group facial">
          {SITES_FACIAL.map(site => (
            <input
              key={site}
              type="number"
              min={0}
              max={15}
              placeholder="–"
              value={row[site] ?? ""}
              className={pocketClass(row[site])}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData(tooth, site, e.target.value)}
              disabled={readOnly}
              title={`${tooth} ${site}`}
            />
          ))}
        </div>

        {/* TOOTH NUMBER BADGE */}
        <div className="tooth-header">{tooth}</div>

        {/* LINGUAL SITES (DL, L, ML) */}
        <div className="site-row-label">L</div>
        <div className="site-group lingual">
          {SITES_LINGUAL.map(site => (
            <input
              key={site}
              type="number"
              min={0}
              max={15}
              placeholder="–"
              value={row[site] ?? ""}
              className={pocketClass(row[site])}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData(tooth, site, e.target.value)}
              disabled={readOnly}
              title={`${tooth} ${site}`}
            />
          ))}
        </div>

      </div>
    );
  };

  return (
    <div className="perio-container horizontal-mode">
      <div className="perio-header-row">
        <strong>Periodontal Chart</strong>
        {readOnly && <span className="muted-text">🔒 Read-only</span>}
      </div>

      <div className="perio-scroll-wrapper">

        {/* UPPER ARCH */}
        <div className="arch-row upper-arch">
          <div className="arch-label">Upper Arch</div>
          <div className="teeth-flex">
            {/* Left quadrant (18–11) */}
            {UPPER_ARCH.slice(0, 8).map(t => renderToothCol(t, true))}
            {/* Midline */}
            <div className="teeth-midline" />
            {/* Right quadrant (21–28) */}
            {UPPER_ARCH.slice(8).map(t => renderToothCol(t, true))}
          </div>
        </div>

        <div className="arch-divider-horizontal" />

        {/* LOWER ARCH */}
        <div className="arch-row lower-arch">
          <div className="arch-label">Lower Arch</div>
          <div className="teeth-flex">
            {/* Left quadrant (48–41) */}
            {LOWER_ARCH.slice(0, 8).map(t => renderToothCol(t, false))}
            {/* Midline */}
            <div className="teeth-midline" />
            {/* Right quadrant (31–38) */}
            {LOWER_ARCH.slice(8).map(t => renderToothCol(t, false))}
          </div>
        </div>

      </div>

      <PerioLegend />
    </div>
  );
}
